import React, { useEffect, useState } from 'react';
import {
  Dialog, Box, Typography, Button, IconButton, alpha, CircularProgress, Zoom,
  Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import dayjs from 'dayjs';
import CloseIcon from '@mui/icons-material/Close';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import { SessaoCaixaService, IItemVendaPdv, IVendaResumoPDV } from '../../shared/services/api/Venda/SessaoCaixaService';
import { PDVThemeColors } from './PDVThemes';

interface DialogItensVendaPDVProps {
  open: boolean;
  onClose: () => void;
  venda: IVendaResumoPDV | null;
  colors: PDVThemeColors;
}

const formatMoeda = (valor: number) =>
  `R$ ${Number(valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// "Ver itens" — clique numa linha da Relação de Vendas (DialogResumoVendasPDV)
// abre isso: só leitura, lista os produtos daquela venda. Busca os itens só
// quando abre (não vêm no resumo geral, que só traz a contagem).
export const DialogItensVendaPDV: React.FC<DialogItensVendaPDVProps> = ({ open, onClose, venda, colors }) => {
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [itens, setItens] = useState<IItemVendaPdv[]>([]);

  useEffect(() => {
    if (!open || !venda) return;
    setCarregando(true);
    setErro('');
    setItens([]);
    SessaoCaixaService.getItensVendaPdv(venda.idmov).then((resultado) => {
      setCarregando(false);
      if (resultado instanceof Error) {
        setErro(resultado.message);
        return;
      }
      setItens(resultado);
    });
  }, [open, venda]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Zoom}
      TransitionProps={{ timeout: 200 }}
      sx={{ '& .MuiDialog-paper': { borderRadius: '16px', overflow: 'hidden', maxHeight: 'calc(100vh - 64px)' } }}
    >
      <Box sx={{
        px: 2.5, py: 1.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        borderBottom: `0.5px solid ${alpha('#000', 0.08)}`,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Box sx={{
            width: 34, height: 34, borderRadius: '8px', background: colors.primaryGradient, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Inventory2RoundedIcon sx={{ fontSize: 18, color: colors.textOnPrimary }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={600} noWrap>Itens da venda #{venda?.numero}</Typography>
            {venda && (
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PersonOutlineRoundedIcon sx={{ fontSize: 13 }} />
                {venda.nomeParceiro} · {dayjs(venda.emissao).format('DD/MM/YYYY HH:mm')}
              </Typography>
            )}
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: 'text.secondary' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ p: 2.5, overflowY: 'auto' }}>
        {carregando ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={26} />
          </Box>
        ) : erro ? (
          <Typography color="error" variant="body2" sx={{ textAlign: 'center', py: 2 }}>{erro}</Typography>
        ) : (
          <Box sx={{ border: `0.5px solid ${alpha('#000', 0.1)}`, borderRadius: '10px', overflow: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Produto</TableCell>
                  <TableCell align="right">Qtd.</TableCell>
                  <TableCell align="right">Un.</TableCell>
                  <TableCell align="right">Preço</TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {itens.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                        Nenhum item encontrado.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : itens.map((item) => (
                  <TableRow key={item.iditens}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{item.descricao}</Typography>
                      {(item.marca || item.referencia) && (
                        <Typography variant="caption" color="text.secondary">
                          {[item.marca, item.referencia].filter(Boolean).join(' · ')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">{item.qtds}</TableCell>
                    <TableCell align="right">{item.un}</TableCell>
                    <TableCell align="right">{formatMoeda(item.preco)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{formatMoeda(item.total || item.subtotal)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Box>

      <Box sx={{ px: 2.5, py: 1.5, borderTop: `0.5px solid ${alpha('#000', 0.08)}`, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          onClick={onClose}
          sx={{ borderRadius: '10px', background: colors.primaryGradient, color: colors.textOnPrimary, '&:hover': { background: colors.hoverGradient } }}
        >
          Fechar
        </Button>
      </Box>
    </Dialog>
  );
};
